import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import './VideoPlayerModal.css';

const VideoPlayerModal = ({ video, isOpen, onClose }) => {
    const [bunnyEmbedUrl, setBunnyEmbedUrl] = useState(null);
    const [bunnyError, setBunnyError] = useState(null);
    const [loadingBunnyUrl, setLoadingBunnyUrl] = useState(false);

    const isBunnyVideo = !!video?.bunnyVideoId;

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    // Bunny videos are access-controlled: mint a fresh signed URL per view
    // instead of embedding a permanent public link.
    useEffect(() => {
        if (!isOpen || !isBunnyVideo) {
            setBunnyEmbedUrl(null);
            setBunnyError(null);
            return;
        }

        let cancelled = false;
        setLoadingBunnyUrl(true);
        setBunnyError(null);

        const getSignedVideoUrl = httpsCallable(functions, 'getSignedVideoUrl');
        getSignedVideoUrl({ videoId: video.id })
            .then(({ data }) => {
                if (!cancelled) setBunnyEmbedUrl(data.embedUrl);
            })
            .catch((err) => {
                console.error('Error fetching signed video URL:', err);
                if (!cancelled) setBunnyError(err.message || 'Unable to load this video.');
            })
            .finally(() => {
                if (!cancelled) setLoadingBunnyUrl(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, isBunnyVideo, video?.id]);

    if (!isOpen || !video) return null;

    const vimeoId = !isBunnyVideo && (video.vimeoId || video.vimeoUrl?.match(/(\d+)/)?.[1]);

    if (!isBunnyVideo && !vimeoId) {
        return (
            <div className="video-player-modal-overlay" onClick={onClose}>
                <div className="video-player-modal-content" onClick={(e) => e.stopPropagation()}>
                    <button className="video-player-modal-close" onClick={onClose}>×</button>
                    <div className="video-player-modal-error">
                        <h3>Video Not Available</h3>
                        <p>Unable to extract Vimeo ID from the video URL. Please check the video configuration.</p>
                    </div>
                </div>
            </div>
        );
    }

    const handleOverlayClick = (e) => {
        // Only close if clicking directly on the overlay background (not on content or video area)
        const target = e.target;

        // Don't close if clicking on video area or modal content
        const isVideoArea = target.tagName === 'IFRAME' ||
                           target.closest('iframe') ||
                           target.closest('.video-player-container') ||
                           target.closest('.video-player-responsive-wrapper') ||
                           target.closest('.video-player-border');

        const isModalContent = target.closest('.video-player-modal-content');

        // Only close if clicking directly on overlay background
        if (e.target === e.currentTarget && !isVideoArea && !isModalContent) {
            onClose();
        }
    };

    const handleHeaderClick = (e) => {
        // Only stop propagation for header to prevent modal from closing when clicking header text
        // But allow back button clicks to work
        if (e.target.classList.contains('video-player-modal-back')) {
            return; // Let the back button handle its own click
        }
        e.stopPropagation();
    };

    const handleBackClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
    };

    const handleCloseClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
    };

    // Build Vimeo embed URL with parameters to disable sharing and prevent navigation
    // title=0, byline=0, portrait=0: Hide video info overlays (prevents clicking to go to Vimeo)
    // badge=0: Hide Vimeo logo in bottom right (prevents clicking to go to Vimeo)
    // autopause=0: Don't pause when another video plays (user controls it)
    // controls=1: Explicitly enable player controls (play, pause, scrubber)
    // share=0: Disable share button
    // like=0: Disable like button
    // dnt=1: Do not track (privacy)
    // responsive=1: Responsive player
    // player_id=0, app_id=58479: Vimeo tracking parameters
    const embedUrl = isBunnyVideo
        ? bunnyEmbedUrl
        : `https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&badge=0&autopause=0&controls=1&share=0&like=0&dnt=1&responsive=1&player_id=0&app_id=58479`;

    return (
        <div className="video-player-modal-overlay" onClick={handleOverlayClick}>
            <div className="video-player-modal-content">
                {/* Video container with Dannytopia border - NO onClick handlers to allow iframe to handle all events */}
                <div className="video-player-container">
                    <div className="video-player-border">
                        {/* Use Vimeo's official responsive wrapper pattern */}
                        <div className="video-player-responsive-wrapper">
                            {isBunnyVideo && loadingBunnyUrl && (
                                <div className="video-player-modal-error">
                                    <p>Loading video...</p>
                                </div>
                            )}
                            {isBunnyVideo && bunnyError && (
                                <div className="video-player-modal-error">
                                    <h3>Video Not Available</h3>
                                    <p>{bunnyError}</p>
                                </div>
                            )}
                            {embedUrl && (
                                <iframe
                                    src={embedUrl}
                                    className="video-player-iframe"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    title={video.title || 'Video'}
                                />
                            )}
                        </div>
                        <button
                            className="video-player-modal-back"
                            onClick={handleBackClick}
                            aria-label="Back to videos"
                            title="Back to videos"
                        >
                            ← Back to Videos
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoPlayerModal;
